import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiFileText, FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiEye, FiBookOpen, FiPlus, FiTrash2, FiEdit3, FiPaperclip, FiRefreshCw, FiSliders, FiClock } from 'react-icons/fi';
import { utcToChicago, getWeekEndDate, formatUTCToChicago, nowChicago } from '../../utils/datetime-comprehensive';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import quizApiService from '../../services/quizApi';
import AssignmentSubmissionInline from './AssignmentSubmissionInline';
import CreateAssignmentInline from './CreateAssignmentInline';
import EditAssignmentModal from './EditAssignmentModal';
import Notification from '../common/Notification';
import PdfViewer from '../common/PdfViewer';
import ConfirmationModal from '../modals/ConfirmationModal';
import ExtensionRequestModal from '../extensions/ExtensionRequestModal';
import logger from '../../utils/logger';
import styles from './Assignments.module.css';
import richTextStyles from '../../styles/richTextViewer.module.css';

// Keeping only ActionButton styled component for instructor actions
const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
  
  &.primary {
    background-color: var(--primary-color);
    color: #fff;
    
    &:hover {
      background-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(184, 149, 106, 0.24);
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-secondary);
      border-color: var(--primary-color);
    }
  }
  
  &.success {
    background-color: var(--success-color);
    color: #fff;
    
    &:hover {
      background-color: var(--success-dark);
      transform: translateY(-1px);
    }
  }
  
  &.revise {
    background-color: rgba(52, 152, 219, 0.1);
    border: 1px solid #3498db;
    color: #2980b9;
    
    &:hover {
      background-color: #3498db;
      color: #fff;
    }
  }
  
  &.danger {
    background-color: var(--error-color);
    color: #fff;
    
    &:hover {
      background-color: var(--error-dark);
    }
    
    &:disabled {
      background: var(--error-light);
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
    justify-content: center;
  }
`;

function Assignments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('posted_desc');
  // Default to 'active' for instructors, 'all' for students
  const [courseFilter, setCourseFilter] = useState(user?.role === 'instructor' || user?.role === 'admin' ? 'active' : 'all');
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSubmissionInline, setShowSubmissionInline] = useState(false);
  const [resubmissionAllowed, setResubmissionAllowed] = useState({});
  const [showCreateInline, setShowCreateInline] = useState(false);
  const [showFullInstructions, setShowFullInstructions] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const location = useLocation();
  const [inlineAssignmentTrigger, setInlineAssignmentTrigger] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignmentPendingDelete, setAssignmentPendingDelete] = useState(null);
  const [notification, setNotification] = useState({ isOpen: false });
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfData, setCurrentPdfData] = useState(null);
  const [currentPdfFileName, setCurrentPdfFileName] = useState('');

  // Extension requests — keyed by `assignment-{id}` for the current student.
  const [extensionsByKey, setExtensionsByKey] = useState({});
  const [extensionTarget, setExtensionTarget] = useState(null);

  const loadMyExtensions = useCallback(async () => {
    if (user?.role !== 'student') return;
    try {
      const res = await apiService.listMyExtensions();
      if (res?.success && Array.isArray(res.requests)) {
        const map = {};
        for (const r of res.requests) {
          const key = `${r.item_type}-${r.item_id}`;
          // Keep the most recent per item (already sorted desc by API)
          if (!map[key]) map[key] = r;
        }
        setExtensionsByKey(map);
      }
    } catch (err) {
      logger.error('Failed to load extension requests', { err });
    }
  }, [user?.role]);

  useEffect(() => { loadMyExtensions(); }, [loadMyExtensions]);

  const getStatus = useCallback((assignment) => {
    // Weekly week-specific status should be derived from submitted_week_numbers,
    // not the base row's submitted_at/status (which may point to a different week).
    if (assignment.allow_weekly_submissions && assignment.week_number) {
      let submittedWeeks = assignment.submitted_week_numbers || [];
      let draftWeeks = assignment.draft_week_numbers || [];
      if (typeof submittedWeeks === 'string') {
        try {
          submittedWeeks = JSON.parse(submittedWeeks);
        } catch (e) {
          submittedWeeks = [];
        }
      }
      if (typeof draftWeeks === 'string') {
        try {
          draftWeeks = JSON.parse(draftWeeks);
        } catch (e) {
          draftWeeks = [];
        }
      }

      const isSubmittedForThisWeek = Array.isArray(submittedWeeks)
        ? submittedWeeks.some(w => Number(w) === Number(assignment.week_number))
        : false;
      const isDraftForThisWeek = Array.isArray(draftWeeks)
        ? draftWeeks.some(w => Number(w) === Number(assignment.week_number))
        : false;

      if (isSubmittedForThisWeek) {
        return 'submitted';
      }

      if (isDraftForThisWeek) {
        return 'draft';
      }
    }

    // Use the status from the backend API if available
    if (assignment.status) {
      return assignment.status;
    }
    
    // Fallback logic if backend status is not available
    // Check if assignment has been graded (has a grade value)
    if (assignment.grade !== null && assignment.grade !== undefined) {
      return 'graded';
    }
    
    // Check if assignment has been submitted (has submitted_at timestamp)
    if (assignment.submitted_at !== null && assignment.submitted_at !== undefined) {
      const dueDate = new Date(assignment.due_date);
      const submittedDate = new Date(assignment.submitted_at);
      return submittedDate > dueDate ? 'late' : 'submitted';
    }
    
    // If no submitted_at but resubmission is allowed, this means the assignment was previously submitted
    // but is now a draft for resubmission. We should still show it as 'submitted' in the list
    // because the student has already submitted it once
    if (resubmissionAllowed[assignment.id]) {
      return 'submitted'; // This is a draft for resubmission, but show as submitted
    }
    // Otherwise it's pending/draft
    return 'pending';
  }, [resubmissionAllowed]);

  const computeStatusType = useCallback((assignment) => {
    // Weekly week-specific completion must be based on submitted_week_numbers.
    if (assignment.allow_weekly_submissions && assignment.week_number) {
      let submittedWeeks = assignment.submitted_week_numbers || [];
      if (typeof submittedWeeks === 'string') {
        try {
          submittedWeeks = JSON.parse(submittedWeeks);
        } catch (e) {
          submittedWeeks = [];
        }
      }

      const isSubmittedForThisWeek = Array.isArray(submittedWeeks)
        ? submittedWeeks.some(w => Number(w) === Number(assignment.week_number))
        : false;

      if (isSubmittedForThisWeek) {
        return 'completed';
      }

      if (assignment.is_extra_credit) {
        return 'upcoming';
      }
    }

    if (assignment.submitted_at || (assignment.grade !== null && assignment.grade !== undefined)) {
      return 'completed';
    }

    // For weekly assignments, use display_due_date (the calculated due date for the current week)
    // For regular assignments, use due_date
    let dueDate;
    if (assignment.allow_weekly_submissions && assignment.display_due_date) {
      // For weekly assignments with a calculated display_due_date, convert from Luxon DateTime
      try {
        if (typeof assignment.display_due_date === 'string') {
          dueDate = new Date(assignment.display_due_date);
        } else if (assignment.display_due_date && typeof assignment.display_due_date.toISO === 'function') {
          dueDate = new Date(assignment.display_due_date.toISO());
        } else {
          dueDate = null;
        }
      } catch (e) {
        dueDate = null;
      }
    } else {
      dueDate = assignment.due_date ? new Date(assignment.due_date) : null;
    }

    if (!dueDate || isNaN(dueDate.getTime())) {
      return 'upcoming';
    }

    const now = new Date();
    if (now > dueDate) {
      return 'overdue';
    }

    const diffDays = (dueDate - now) / (1000 * 60 * 60 * 24);
    if (diffDays <= 3) {
      return 'dueSoon';
    }

    return 'upcoming';
  }, []);

  const computeDisplayStatus = useCallback((assignment) => {
    const status = getStatus(assignment);
    if (status === 'late') {
      return { key: 'late', label: 'Late', icon: <FiAlertTriangle size={14} /> };
    }

    // Do not show an "Overdue" badge in My Assignments cards.
    return null;
  }, [getStatus]);

  const getAllTabStatus = useCallback((assignment) => {
    if (assignment.statusType === 'completed') {
      return { key: 'completed', label: 'Completed' };
    }

    if (assignment.statusType === 'overdue') {
      return { key: 'overdue', label: 'Overdue' };
    }

    return { key: 'upcoming', label: 'Open' };
  }, []);

  const getAssignmentDueDate = useCallback((assignment) => {
    if (assignment?.is_extra_credit && !assignment?.display_due_date) {
      return null;
    }

    if (assignment.allow_weekly_submissions && assignment.display_due_date) {
      if (typeof assignment.display_due_date === 'string') {
        return new Date(assignment.display_due_date);
      }

      if (assignment.display_due_date && typeof assignment.display_due_date.toISO === 'function') {
        return new Date(assignment.display_due_date.toISO());
      }
    }

    return assignment.due_date ? new Date(assignment.due_date) : null;
  }, []);

  const isLateSubmission = useCallback((assignment, status) => {
    if (!assignment.submitted_at) return false;

    const dueDate = getAssignmentDueDate(assignment);
    if (!dueDate || isNaN(dueDate.getTime())) return false;

    const submittedAt = new Date(assignment.submitted_at);
    if (isNaN(submittedAt.getTime())) return false;

    return submittedAt.getTime() > dueDate.getTime();
  }, [getAssignmentDueDate]);

  const toggleInstructionsVisibility = useCallback((assignmentId) => {
    setShowFullInstructions(prev => ({
      ...prev,
      [assignmentId]: !prev[assignmentId]
    }));
  }, []);

  const getAssignmentUiKey = useCallback((assignment) => {
    return assignment?.allow_weekly_submissions && assignment?.week_number
      ? `${assignment.id}-week-${assignment.week_number}`
      : `${assignment?.id}`;
  }, []);

  const getWeeklyPostedDate = useCallback((assignment, weekNumber) => {
    if (!assignment?.start_date || !weekNumber || Number(weekNumber) < 1) {
      return null;
    }

    if (Number(weekNumber) === 1) {
      return assignment.start_date;
    }

    const previousWeekEnd = getWeekEndDate(
      assignment.start_date,
      Number(weekNumber) - 1,
      Array.isArray(assignment.break_periods) ? assignment.break_periods : []
    );

    return previousWeekEnd?.toUTC().toISO() || null;
  }, []);

  const getPrimaryActionLabel = useCallback((assignment, status) => {
    if (['submitted', 'late', 'graded'].includes(status)) {
      return resubmissionAllowed[assignment.id] ? 'Revise Assignment' : 'View Submission';
    }

    if (status === 'draft') {
      return 'Resume Assignment';
    }

    return 'Start Assignment';
  }, [resubmissionAllowed]);

  // Process weekly assignments to add display_due_date and week_number
  // Apply the same logic as the Dashboard component
  const processWeeklyAssignments = useCallback((assignmentsList) => {
    const processedAssignments = [];

    assignmentsList.forEach(assignment => {
      if (!assignment.allow_weekly_submissions) {
        processedAssignments.push(assignment);
        return;
      }

      const now = nowChicago();
      const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
      const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
        ? assignment.weekly_overrides_by_week
        : {};

      // Get list of submitted weeks
      let submittedWeeks = [];
      let draftWeeks = [];
      if (assignment.submitted_week_numbers) {
        submittedWeeks = assignment.submitted_week_numbers;
        if (typeof submittedWeeks === 'string') {
          try {
            submittedWeeks = JSON.parse(submittedWeeks);
          } catch (e) {
            submittedWeeks = [];
          }
        }
        if (!Array.isArray(submittedWeeks)) {
          submittedWeeks = [];
        }
      }
      if (assignment.draft_week_numbers) {
        draftWeeks = assignment.draft_week_numbers;
        if (typeof draftWeeks === 'string') {
          try {
            draftWeeks = JSON.parse(draftWeeks);
          } catch (e) {
            draftWeeks = [];
          }
        }
        if (!Array.isArray(draftWeeks)) {
          draftWeeks = [];
        }
      }

      // Respect assignment max weekly submissions (fallback to 1)
      const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
      const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
        ? Math.floor(parsedMaxWeeks)
        : 1;

      // Calculate overdue weeks and next open week
      const overdueWeeks = [];
      const completedWeeks = [];
      let nextOpenWeek = null;
      let nextOpenWeekDueDate = null;

      for (let weekNumber = 1; weekNumber <= maxWeeks; weekNumber++) {
        let weekDueDate;
        const weekOverride = weeklyOverridesByWeek[weekNumber] ?? weeklyOverridesByWeek[String(weekNumber)];
        const isExtraCreditWeek = weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1;

        if (weekNumber === 1 && assignment.due_date) {
          weekDueDate = utcToChicago(assignment.due_date);
        } else if (weekOverride?.due_date) {
          weekDueDate = utcToChicago(weekOverride.due_date);
        } else if (assignment.start_date) {
          weekDueDate = getWeekEndDate(assignment.start_date, weekNumber, breakPeriods);
        } else {
          continue;
        }

        if (!weekDueDate && !isExtraCreditWeek) continue;

        // Check if submitted for this week
        const isSubmittedForThisWeek = submittedWeeks.some(w => Number(w) === weekNumber);
        const isDraftForThisWeek = draftWeeks.some(w => Number(w) === weekNumber);

        // Collect submitted weeks
        if (isSubmittedForThisWeek) {
          completedWeeks.push({
            week_number: weekNumber,
            display_due_date: isExtraCreditWeek ? null : weekDueDate,
            override: weekOverride
          });
          continue;
        }

        if (isExtraCreditWeek) {
          if (!nextOpenWeek) {
            nextOpenWeek = weekNumber;
            nextOpenWeekDueDate = null;
          }
          continue;
        }

        // Categorize unsubmitted weeks as overdue or pending
        if (weekDueDate < now) {
          // Overdue: due date is in the past
          overdueWeeks.push({
            week_number: weekNumber,
            display_due_date: weekDueDate,
            isDraft: isDraftForThisWeek
          });
        } else if (!nextOpenWeek) {
          // This is the next unsubmitted week with a future due date
          // Only show it if:
          // 1. It's Week 1, OR
          // 2. The immediately preceding week's deadline has passed
          let canShowThisWeek = false;

          if (weekNumber === 1) {
            // Week 1 is always available
            canShowThisWeek = true;
          } else {
            // Check if the deadline of week (weekNumber - 1) has passed
            let previousWeekDueDate;
            if (weekNumber === 2 && assignment.due_date) {
              previousWeekDueDate = utcToChicago(assignment.due_date);
            } else if (assignment.start_date) {
              previousWeekDueDate = getWeekEndDate(assignment.start_date, weekNumber - 1, breakPeriods);
            }

            // Only show this week if the immediately preceding week's deadline has passed
            if (previousWeekDueDate && previousWeekDueDate <= now) {
              canShowThisWeek = true;
            }
          }

          if (canShowThisWeek || isDraftForThisWeek) {
            nextOpenWeek = weekNumber;
            nextOpenWeekDueDate = weekDueDate;
            // Found the next open week, break since we only want the next open one
            break;
          }
        }

        // If weekDueDate is too far in the future, break the loop
        if (weekDueDate > now.plus({ weeks: 8 })) {
          break;
        }
      }

      // Helper to create week-specific assignment object
      const createWeekAssignment = (weekNum, dueDate, isSubmitted) => {
        const weeklyInstructionsByWeek = assignment?.weekly_instructions_by_week && typeof assignment.weekly_instructions_by_week === 'object'
          ? assignment.weekly_instructions_by_week
          : {};
        const customWeekInstruction = weeklyInstructionsByWeek[weekNum] ?? weeklyInstructionsByWeek[String(weekNum)];
        const customWeekInstructionText = typeof customWeekInstruction === 'string'
          ? customWeekInstruction
          : customWeekInstruction?.instructions;
        const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
          ? assignment.weekly_overrides_by_week
          : {};
        const weekOverride = weeklyOverridesByWeek[weekNum] ?? weeklyOverridesByWeek[String(weekNum)];
        const isExtraCredit = weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1;

        const weekAssignment = {
          ...assignment,
          week_number: weekNum,
          display_due_date: isExtraCredit && !weekOverride?.due_date ? null : dueDate,
          due_date: isExtraCredit && !weekOverride?.due_date ? null : assignment.due_date,
          is_required: weekOverride?.is_required !== undefined ? Boolean(weekOverride.is_required) : true,
          is_extra_credit: isExtraCredit,
          posted_date: getWeeklyPostedDate(assignment, weekNum) || assignment.posted_date,
          description: (typeof customWeekInstructionText === 'string' && customWeekInstructionText.trim().length > 0)
            ? customWeekInstructionText
            : assignment.description,
        };

        // Clear submitted_at and status if THIS week wasn't submitted
        if (!isSubmitted) {
          weekAssignment.submitted_at = null;
          weekAssignment.status = draftWeeks.some(w => Number(w) === Number(weekNum)) ? 'draft' : null;
        }

        return weekAssignment;
      };

      // Add completed weeks
      completedWeeks.forEach(week => {
        const weekAssignment = createWeekAssignment(week.week_number, week.display_due_date, true);
        processedAssignments.push(weekAssignment);
      });

      // Add overdue weeks
      overdueWeeks.forEach(week => {
        const weekAssignment = createWeekAssignment(week.week_number, week.display_due_date, false);
        processedAssignments.push(weekAssignment);
      });

      // Add next open week
      if (nextOpenWeek) {
        const weekAssignment = createWeekAssignment(nextOpenWeek, nextOpenWeekDueDate, false);
        processedAssignments.push(weekAssignment);
      }

      // If no submitted, overdue, or open weeks found, add the base assignment
      if (completedWeeks.length === 0 && overdueWeeks.length === 0 && !nextOpenWeek) {
        processedAssignments.push(assignment);
      }
    });

    // Deduplicate: Remove any duplicate entries based on assignment.id + week_number combination
    const seen = new Set();
    const deduplicatedAssignments = processedAssignments.filter(assignment => {
      const key = assignment.allow_weekly_submissions && assignment.week_number
        ? `${assignment.id}-week-${assignment.week_number}`
        : `${assignment.id}`;

      if (seen.has(key)) {
        return false; // Skip duplicate
      }
      seen.add(key);
      return true;
    });

    const weeklyDescendingAssignments = [...deduplicatedAssignments].sort((a, b) => {
      const sameAssignment = Number(a.id) === Number(b.id);
      const aWeek = Number(a.week_number);
      const bWeek = Number(b.week_number);
      const bothWeeklyRows = Number.isFinite(aWeek) && aWeek > 0 && Number.isFinite(bWeek) && bWeek > 0;

      if (sameAssignment && bothWeeklyRows) {
        return bWeek - aWeek;
      }

      return 0;
    });

    return weeklyDescendingAssignments;
  }, [getWeeklyPostedDate]);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.getAssignments();
      if (response.success) {
        let assignments = response.assignments || [];
        // Process weekly assignments to add display_due_date and week_number
        assignments = processWeeklyAssignments(assignments);
        setAssignments(assignments);
        
        // Load resubmission status from localStorage first
        const savedResubmissionState = localStorage.getItem('resubmissionAllowed');
        let currentResubmissionState = {};
        if (savedResubmissionState) {
          try {
            const parsedState = JSON.parse(savedResubmissionState);
            setResubmissionAllowed(parsedState);
            currentResubmissionState = parsedState;
          } catch (error) {
            console.error('Error parsing saved resubmission state:', error);
          }
        }
        
        // Check resubmission status for submitted assignments
        // Only check if we don't already have the resubmission status
        assignments.forEach(assignment => {
          // Use inline status logic to avoid circular dependency
          let status = assignment.status;
          if (!status) {
            if (assignment.grade !== null && assignment.grade !== undefined) {
              status = 'graded';
            } else if (assignment.submitted_at !== null && assignment.submitted_at !== undefined) {
              status = 'submitted';
            } else {
              status = 'pending';
            }
          }
          
          if ((status === 'submitted' || status === 'graded' || status === 'late') && 
              !currentResubmissionState.hasOwnProperty(assignment.id)) {
            checkResubmissionStatus(assignment.id);
          }
        });
      } else {
        setError('Failed to load assignments');
      }
    } catch (error) {
      logger.error('Error fetching assignments', { error });
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [processWeeklyAssignments]);

  const fetchCourses = useCallback(async () => {
    if (user?.role === 'instructor' || user?.role === 'admin') {
      try {
        const response = await quizApiService.getCourses();
        if (response.success) {
          setCourses(response.courses || response.data || []);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
      }
    }
  }, [user?.role]);

  useEffect(() => {
    fetchAssignments();
    fetchCourses();
  }, [user?.role, fetchAssignments, fetchCourses]);

  // Reset course filter when user role changes
  useEffect(() => {
    if (user?.role === 'instructor' || user?.role === 'admin') {
      setCourseFilter('active');
    } else {
      setCourseFilter('all');
    }
  }, [user?.role]);

  const assignmentsWithStatus = useMemo(() => {
    return assignments.map(assignment => ({
      ...assignment,
      statusType: computeStatusType(assignment)
    }));
  }, [assignments, computeStatusType]);

  // Build comprehensive course list from assignments and separate active/past
  const { activeCourses, inactiveCourses } = useMemo(() => {
    const courseMap = new Map();
    
    // Collect unique courses from assignments
    assignments.forEach(assignment => {
      if (assignment.course_id && assignment.course_name) {
        const courseId = assignment.course_id.toString();
        if (!courseMap.has(courseId)) {
          courseMap.set(courseId, {
            id: courseId,
            name: assignment.course_name,
            code: assignment.course_code || '',
            is_active: assignment.course_is_active === 1 || assignment.course_is_active === true,
            end_date: assignment.course_end_date
          });
        }
      }
    });

    const allCourses = Array.from(courseMap.values());
    const active = allCourses.filter(course => {
      const isActive = course.is_active;
      const hasNoEndDate = !course.end_date;
      const endDateInFuture = course.end_date ? new Date(course.end_date) >= new Date() : false;
      return isActive && (hasNoEndDate || endDateInFuture);
    });
    const activeIds = new Set(active.map(c => c.id));
    const inactive = allCourses.filter(c => !activeIds.has(c.id));

    return {
      activeCourses: active.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      inactiveCourses: inactive.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    };
  }, [assignments]);

  // Set of course IDs currently considered active — used to decide whether
  // an assignment needs an extension to submit.
  const activeCourseIdSet = useMemo(
    () => new Set(activeCourses.map((c) => String(c.id))),
    [activeCourses]
  );

  // Per-card extension/access derivation. Centralized so both the JSX and
  // the submit-handler guard share the same view.
  const getExtensionInfo = useCallback((assignment) => {
    const courseActive = activeCourseIdSet.has(String(assignment.course_id));
    const ext = extensionsByKey[`assignment-${assignment.id}`] || null;
    const expiresMs = ext?.access_expires_at ? new Date(ext.access_expires_at).getTime() : 0;
    const now = Date.now();
    const approvedActive = ext?.status === 'approved' && expiresMs > now;
    const approvedExpired = ext?.status === 'approved' && expiresMs > 0 && expiresMs <= now;
    return {
      ext,
      courseActive,
      approvedActive,
      approvedExpired,
      pending: ext?.status === 'pending',
      denied: ext?.status === 'denied',
      // Submission gate: only blocks when course inactive AND no active extension.
      submissionBlocked: !courseActive && !approvedActive,
      // Show the request CTA when there's no active extension and no pending one.
      canRequest: !courseActive && !approvedActive && ext?.status !== 'pending',
    };
  }, [activeCourseIdSet, extensionsByKey]);

  // Filter assignments by course selection first
  const courseFilteredAssignments = useMemo(() => {
    return assignmentsWithStatus.filter(assignment => {
      if (user?.role === 'instructor' || user?.role === 'admin') {
        // Instructor filtering: 'all', 'active', 'inactive', or specific course ID
        if (courseFilter === 'active') {
          // Show only assignments for active courses
          if (assignment.course_id && courses.length > 0) {
            const course = courses.find(c => c.id === parseInt(assignment.course_id));
            return course ? (course.is_active === 1 || course.is_active === true) : false;
          } else {
            return assignment.course_is_active === 1 || assignment.course_is_active === true;
          }
        } else if (courseFilter === 'inactive') {
          // Show only assignments for inactive courses
          if (assignment.course_id && courses.length > 0) {
            const course = courses.find(c => c.id === parseInt(assignment.course_id));
            if (course) {
              return course.is_active === 0 || course.is_active === false || course.is_active === null || course.is_active === undefined;
            } else {
              return false;
            }
          } else {
            return assignment.course_is_active === 0 || assignment.course_is_active === false || assignment.course_is_active === null || assignment.course_is_active === undefined;
          }
        } else if (courseFilter === 'all') {
          // Show all courses (active and inactive)
          return true;
        } else {
          // courseFilter is a specific course ID
          return assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
        }
      } else {
        // Student filtering: 'all' or specific course ID
        if (courseFilter !== 'all') {
          return assignment.course_id?.toString() === courseFilter;
        }
        // When "all" is selected, only show assignments from active courses
        return assignment.course_is_active === 1 || assignment.course_is_active === true;
      }
    });
  }, [assignmentsWithStatus, courseFilter, courses, user?.role]);

  const tabCounts = useMemo(() => {
    // Only calculate tab counts for students
    if (user?.role === 'instructor' || user?.role === 'admin') {
      return {};
    }
    return courseFilteredAssignments.reduce((acc, assignment) => {
      const key = assignment.statusType || 'upcoming';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [courseFilteredAssignments, user?.role]);

  const tabs = useMemo(() => {
    // Only show tabs for students
    if (user?.role === 'instructor' || user?.role === 'admin') {
      return [];
    }
    return [
      { key: 'all', label: 'All', count: courseFilteredAssignments.length },
      { key: 'upcoming', label: 'Open', count: ((tabCounts.upcoming || 0) + (tabCounts.dueSoon || 0)) },
      { key: 'completed', label: 'Completed', count: tabCounts.completed || 0 },
      { key: 'overdue', label: 'Overdue', count: tabCounts.overdue || 0 }
    ];
  }, [courseFilteredAssignments.length, tabCounts, user?.role]);

  const handleSubmitAssignment = useCallback(async (assignment, weekNumber = null) => {
    try {
      // For weekly submissions, fetch all submissions
      const isWeeklySubmission = assignment.allow_weekly_submissions === 1 || assignment.allow_weekly_submissions === true;

      if (isWeeklySubmission) {
        const response = await apiService.getMyAssignmentSubmissions(assignment.id);
        if (response.success && response.submissions && response.submissions.length > 0) {
          // If weekNumber is provided, find submission for that week
          let selectedSubmission = null;
          if (weekNumber) {
            selectedSubmission = response.submissions.find(s => s.week_number === Number(weekNumber));
          }
          // If no week specified or week not found, use most recent
          if (!selectedSubmission) {
            selectedSubmission = response.submissions.sort((a, b) => {
              if (a.week_number && b.week_number) {
                return b.week_number - a.week_number; // Most recent week first
              }
              return 0;
            })[0];
          }
          setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber || selectedSubmission.week_number });
          setSelectedSubmission(selectedSubmission);
          setShowSubmissionInline(true);
        } else {
          // No submissions yet
          setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber });
          setSelectedSubmission(null);
          setShowSubmissionInline(true);
        }
      } else {
        // Regular submission - fetch single submission
        const response = await apiService.getAssignmentSubmission(assignment.id);
        if (response.success) {
          setSelectedAssignment(assignment);
          setSelectedSubmission(response.submission);
          setShowSubmissionInline(true);
        } else {
          // No existing submission, open inline view with empty content
          setSelectedAssignment(assignment);
          setSelectedSubmission(null);
          setShowSubmissionInline(true);
        }
      }
    } catch (error) {
      // Check if this is a "not submitted" error (404) vs a real error
      if (error.message && error.message.includes('not submitted')) {
        // This is expected - user hasn't submitted yet, open empty submission view
        setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber });
        setSelectedSubmission(null);
        setShowSubmissionInline(true);
      } else {
        // This is a real error, log it
        logger.error('Error fetching submission', { error, assignmentId: assignment.id });
        setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber });
        setSelectedSubmission(null);
        setShowSubmissionInline(true);
      }
    }
    // Scroll to top when opening assignment submission
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const assignmentIdFromQuery = params.get('assignmentId');
    const weekNumberFromQuery = params.get('weekNumber');
    if (!assignmentIdFromQuery) {
      if (inlineAssignmentTrigger) {
        setInlineAssignmentTrigger(null);
      }
      return;
    }

    if (inlineAssignmentTrigger === assignmentIdFromQuery) {
      return;
    }

    setInlineAssignmentTrigger(assignmentIdFromQuery);

    const openAssignmentInline = async () => {
      const assignmentFromList = assignments.find(assignment => String(assignment.id) === assignmentIdFromQuery);
      try {
        if (assignmentFromList) {
          await handleSubmitAssignment(assignmentFromList, weekNumberFromQuery);
          return;
        }

        const response = await apiService.getAssignment(assignmentIdFromQuery);
        if (response?.success && response.assignment) {
          await handleSubmitAssignment(response.assignment, weekNumberFromQuery);
        } else {
          logger.warn('Assignment not found for inline view', { assignmentId: assignmentIdFromQuery, response });
        }
      } catch (error) {
        logger.error('Failed to open assignment inline from query', { error, assignmentId: assignmentIdFromQuery });
      }
    };

    openAssignmentInline();
  }, [location.search, assignments, inlineAssignmentTrigger, handleSubmitAssignment]);

  const handleViewSubmissions = (assignment) => {
    navigate(`/assignments/${assignment.id}/submissions`);
  };

  const handleBackFromSubmission = () => {
    setShowSubmissionInline(false);
    setSelectedAssignment(null);
    setSelectedSubmission(null);
    // Refresh assignments to show updated status
    fetchAssignments();
  };

  const handleBackFromCreate = () => {
    setShowCreateInline(false);
    // Refresh assignments to show the new assignment
    fetchAssignments();
  };


  const handleViewSubmission = (assignment, weekNumber = null) => {
    handleSubmitAssignment(assignment, weekNumber);
  };

  const handleReviseAssignment = async (assignment, weekNumber = null) => {
    try {
      // Fetch existing submission to check resubmission state
      const response = await apiService.getAssignmentSubmission(assignment.id);
      if (response.success) {
        setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber });
        setSelectedSubmission(response.submission);
        setShowSubmissionInline(true);
      } else {
        // No existing submission, open inline view with empty content
        setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber });
        setSelectedSubmission(null);
        setShowSubmissionInline(true);
      }
    } catch (error) {
      console.error('Error fetching submission for revision:', error);
      // Fallback to regular submission flow
      setSelectedAssignment({ ...assignment, _preselectedWeek: weekNumber });
      setSelectedSubmission(null);
      setShowSubmissionInline(true);
    }
    // Scroll to top when opening assignment revision
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const checkResubmissionStatus = async (assignmentId) => {
    try {
      const response = await apiService.getAssignmentSubmission(assignmentId);
      if (response.success && response.submission) {
        const isResubmissionAllowed = response.submission.resubmission_state === 'open_for_resubmission';
        setResubmissionAllowed(prev => {
          const newState = {
            ...prev,
            [assignmentId]: isResubmissionAllowed
          };
          // Save to localStorage to persist across page navigation
          localStorage.setItem('resubmissionAllowed', JSON.stringify(newState));
          return newState;
        });
      }
    } catch (error) {
      logger.error('Error checking resubmission status', { error, assignmentId });
    }
  };

  const handleSubmissionSuccess = (assignmentId) => {
    // Clear resubmission state from localStorage since assignment was submitted
    setResubmissionAllowed(prev => {
      const newState = { ...prev };
      delete newState[assignmentId];
      localStorage.setItem('resubmissionAllowed', JSON.stringify(newState));
      return newState;
    });
    // Refresh assignments to show updated status
    fetchAssignments();
  };


  const handleAssignmentCreated = (newAssignment) => {
    setShowCreateInline(false);
    // Refresh assignments to show the new assignment
    fetchAssignments();
  };

  const handleDeleteAssignment = (assignment) => {
    setAssignmentPendingDelete(assignment);
  };

  const confirmDeleteAssignment = async () => {
    if (!assignmentPendingDelete) return;

    setDeleteLoading(true);
    try {
      await apiService.deleteAssignment(assignmentPendingDelete.id);
      setAssignments(prev => prev.filter(a => a.id !== assignmentPendingDelete.id));
      setAssignmentPendingDelete(null);
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Assignment Deleted',
        message: 'The assignment and its student submissions were deleted successfully.'
      });
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete assignment. Please try again.'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditAssignment = (assignment) => {
    navigate(`/assignments/${assignment.id}/edit`);
  };

  const handleAssignmentUpdated = () => {
    setShowEditModal(false);
    fetchAssignments(); // Refresh the assignments list
  };

  const handleViewAssignmentPdf = (assignment) => {
    if (assignment.pdf_attachment) {
      try {
        let cleanBase64 = assignment.pdf_attachment;
        
        // Remove data URI prefix if present
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        
        // Remove whitespace
        cleanBase64 = cleanBase64.replace(/\s/g, '');
        
        // Validate base64
        try {
          atob(cleanBase64);
          setCurrentPdfData(cleanBase64);
          setCurrentPdfFileName(`${assignment.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_attachment.pdf`);
          setShowPdfViewer(true);
        } catch (decodeError) {
          logger.error('Base64 decode error', { error: decodeError, assignmentId: assignment.id });
          setNotification({
            isOpen: true,
            type: 'error',
            title: 'PDF Error',
            message: 'The PDF attachment data is not in a valid format. Please contact the instructor.'
          });
        }
      } catch (error) {
        console.error('Error processing PDF attachment:', error);
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'PDF Error',
          message: 'Failed to load PDF attachment. Please try again or contact the instructor.'
        });
      }
    } else {
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'PDF Error',
        message: 'No PDF attachment found for this assignment.'
      });
    }
  };

  const formatAssignmentDueDate = (assignment) => {
    if (assignment?.is_extra_credit && !assignment?.display_due_date && !assignment?.due_date) {
      return 'No due date';
    }

    if (assignment?.allow_weekly_submissions && assignment?.week_number && assignment?.display_due_date) {
      return formatUTCToChicago(assignment.display_due_date.toUTC().toISO(), 'MMM d \'·\' h:mm a');
    }

    if (!assignment?.due_date) {
      return 'No due date';
    }

    return formatUTCToChicago(assignment.due_date, 'MMM d \'·\' h:mm a');
  };

  const isInstructor = user?.role === 'instructor' || user?.role === 'admin';

  const filteredAssignments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let filtered = courseFilteredAssignments
      .filter(assignment => {
        if (!term) return true;
        return (assignment.title?.toLowerCase().includes(term)) ||
          (assignment.course_name?.toLowerCase().includes(term));
      });
    
    // Only filter by activeTab for students
    if (user?.role === 'student') {
      filtered = filtered.filter(assignment => {
        if (activeTab === 'all') return true;
        if (activeTab === 'overdue') return assignment.statusType === 'overdue';
        if (activeTab === 'upcoming') return ['upcoming', 'dueSoon'].includes(assignment.statusType);
        if (activeTab === 'completed') return assignment.statusType === 'completed';
        return true;
      });
    }
    
    return [...filtered].sort((a, b) => {
      if (sortBy === 'posted_asc' || sortBy === 'posted_desc') {
        const dateA = new Date(a.posted_date || a.created_at || 0).getTime();
        const dateB = new Date(b.posted_date || b.created_at || 0).getTime();
        return sortBy === 'posted_asc' ? dateA - dateB : dateB - dateA;
      }

      if (sortBy === 'due_asc' || sortBy === 'due_desc') {
        const dateA = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
        const dateB = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
        return sortBy === 'due_asc' ? dateA - dateB : dateB - dateA;
      }

      if (sortBy === 'title_desc') {
        return (b.title || '').localeCompare(a.title || '', undefined, { sensitivity: 'base' });
      }

      return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
    });
  }, [courseFilteredAssignments, searchTerm, activeTab, user?.role, sortBy]);


  return (
    <div className={styles.container}>
      {!showSubmissionInline && !showCreateInline && (
        <div className={styles.heroRow}>
          <header className={styles.hero}>
            <div className={styles.heroEyebrow}>
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <h1 className={styles.heroTitle}>
              {isInstructor ? 'Assignments' : 'My Assignments'}
            </h1>
            <p className={styles.heroSub}>
              {isInstructor
                ? 'Create, review, and grade work across your courses.'
                : 'Track open, completed, and overdue work across your enrolled courses.'}
            </p>
          </header>
          {isInstructor && (
            <ActionButton
              className="primary"
              onClick={() => setShowCreateInline(true)}
            >
              <FiPlus size={16} />
              Create Assignment
            </ActionButton>
          )}
        </div>
      )}
      
      {!showSubmissionInline && !showCreateInline && (
        <>
          {/* Search and Filter Row */}
          <div className={styles.searchFilterRow}>
            <div className={styles.searchBar}>
              <div className={styles.searchIcon}>
                <FiSearch size={16} />
              </div>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search assignments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Course Filter */}
            {(user?.role === 'student' || isInstructor) && (
              <div className={styles.courseFilterContainer}>
                <FiBookOpen className={styles.filterControlIcon} />
                <label className={styles.courseFilterLabel}>Course:</label>
                <select
                  className={styles.courseFilterSelect}
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  {isInstructor ? (
                    <>
                      <option value="all">All Courses</option>
                      <option value="active">Active Courses</option>
                      <option value="inactive">Inactive Courses</option>
                      {courses.length > 0 && (
                        <>
                          <option disabled>──────────</option>
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>
                              {course.code} - {course.name}
                            </option>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <option value="all">All Active Courses</option>
                      {activeCourses.length === 0 && inactiveCourses.length === 0 && (
                        <option value="" disabled>No courses available</option>
                      )}
                      {activeCourses.length > 0 && activeCourses.map(course => (
                        <option key={`course-${course.id}`} value={course.id}>
                          {course.code ? `${course.code} - ${course.name}` : course.name}
                        </option>
                      ))}
                      {inactiveCourses.length > 0 && (
                        <>
                          <option disabled>──────────</option>
                          <option disabled>Past Courses</option>
                          {inactiveCourses.map(course => (
                            <option key={`course-${course.id}`} value={course.id}>
                              {course.code ? `${course.code} - ${course.name}` : course.name}
                            </option>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </select>
              </div>
            )}

            <div className={styles.courseFilterContainer}>
              <FiSliders className={styles.filterControlIcon} />
              <label className={styles.courseFilterLabel}>Sort:</label>
              <select
                className={styles.courseFilterSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="posted_desc">Recently Posted</option>
                <option value="posted_asc">Oldest Posted</option>
                <option value="due_asc">Due Soonest</option>
                <option value="due_desc">Due Latest</option>
                <option value="title_asc">Title A-Z</option>
                <option value="title_desc">Title Z-A</option>
              </select>
            </div>
          </div>

          {/* Status filter cards — students only. Replaces the old tabsRow with
              the same refined editorial KPI grid used elsewhere in the portal. */}
          {user?.role === 'student' && tabs.length > 0 && (
            <div className={styles.statGrid} role="tablist" aria-label="Filter by status">
              {tabs.map((tab, i) => {
                const accentMap = { all: 'gold', upcoming: 'teal', completed: 'sage', overdue: 'terracotta' };
                const captionMap = {
                  all: 'Across selected courses',
                  upcoming: 'Ready to start',
                  completed: 'Submitted work',
                  overdue: 'Needs attention',
                };
                const labelMap = { all: 'All', upcoming: 'Open', completed: 'Completed', overdue: 'Overdue' };
                const accent = accentMap[tab.key] || 'gold';
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    className={`${styles.statCard} ${styles[`accent_${accent}`]} ${activeTab === tab.key ? styles.statActive : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                    style={{ '--stat-delay': `${i * 0.04}s` }}
                  >
                    <span className={styles.statLabel}>{labelMap[tab.key] || tab.label}</span>
                    <span className={styles.statValue}>{tab.count}</span>
                    <span className={styles.statCaption}>{captionMap[tab.key]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {!showSubmissionInline && !showCreateInline && (
        <>
          {loading ? (
            <div className={styles.emptyState}>
              <FiFileText />
              <h3>Loading assignments...</h3>
              <p>Please wait while we fetch your assignments.</p>
            </div>
          ) : error ? (
            <div className={styles.emptyState}>
              <FiAlertCircle />
              <h3>Error loading assignments</h3>
              <p>{error}</p>
            </div>
          ) : filteredAssignments.length > 0 ? (
            <div className={styles.assignmentsList}>
              {filteredAssignments.map((assignment) => {
                const assignmentUiKey = getAssignmentUiKey(assignment);
                const status = getStatus(assignment);
                const displayStatus = computeDisplayStatus(assignment);
                const allTabStatus = getAllTabStatus(assignment);
                const extInfo = !isInstructor ? getExtensionInfo(assignment) : null;
                const formatExpiry = (ts) => {
                  if (!ts) return '';
                  try {
                    return new Date(ts).toLocaleString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    });
                  } catch { return ''; }
                };
                const showLateBadge = ['all', 'completed'].includes(activeTab) && assignment.statusType === 'completed' && isLateSubmission(assignment, status);
                const showStatusBadge = !['upcoming', 'completed'].includes(allTabStatus.key);
                const isWeeklyAssignment = assignment.allow_weekly_submissions === true || Number(assignment.allow_weekly_submissions) === 1;
                const isExtraCreditAssignment = assignment.is_extra_credit === true || assignment.is_extra_credit === 1;
                const hideDueDate = isExtraCreditAssignment && !assignment.display_due_date && !assignment.due_date;
                const showFullText = showFullInstructions[assignmentUiKey];
                const showStudentInstructions = isInstructor || showFullText;
                const primaryLabel = getPrimaryActionLabel(assignment, status);
                const isOverdueAssignment = status === 'overdue' || assignment.statusType === 'overdue';
                const primaryActionClass = status === 'draft'
                  ? styles.resumeAction
                  : resubmissionAllowed[assignment.id]
                    ? styles.reviseAction
                    : isExtraCreditAssignment
                      ? styles.extraCreditAction
                    : ['submitted', 'late', 'graded'].includes(status)
                      ? styles.viewSubmissionAction
                    : isOverdueAssignment
                      ? styles.overdueStartAction
                    : '';

                // Calculate file size for attachment if exists
                let attachmentSize = null;
                if (assignment.pdf_attachment) {
                  let cleanBase64 = assignment.pdf_attachment;
                  if (cleanBase64.includes(',')) {
                    cleanBase64 = cleanBase64.split(',')[1];
                  }
                  cleanBase64 = cleanBase64.replace(/\s/g, '');
                  attachmentSize = ((cleanBase64.length * 3) / 4 / 1024).toFixed(1);
                }

                return (
                  <div key={`assignment-${assignment.id}${assignment.week_number ? `-week-${assignment.week_number}` : ''}`} className={styles.assignmentCard}>
                    <div className={styles.assignmentDesktopLayout}>
                      <div className={styles.assignmentMainColumn}>
                    <div className={styles.statusPillGroup}>
                      {!isInstructor && activeTab === 'all' && showStatusBadge && (
                        <span className={`${styles.statusPill} ${styles[allTabStatus.key]}`}>
                          <span>{allTabStatus.label}</span>
                        </span>
                      )}
                      {!isInstructor && activeTab !== 'all' && displayStatus && (
                        <span className={`${styles.statusPill} ${styles[displayStatus.key]}`}>
                          {displayStatus.icon}
                          <span>{displayStatus.label}</span>
                        </span>
                      )}
                      {!isInstructor && showLateBadge && (
                        <span className={`${styles.statusPill} ${styles.late}`}>
                          <FiAlertTriangle size={14} />
                          <span>Late</span>
                        </span>
                      )}
                      {isExtraCreditAssignment && (
                        <span className={`${styles.statusPill} ${styles.extraCredit}`}>
                          <span>Extra Credit</span>
                        </span>
                      )}
                      {!isInstructor && extInfo && !extInfo.courseActive && (
                        <span className={`${styles.statusPill} ${styles.courseEnded}`}>
                          <span>Course Ended</span>
                        </span>
                      )}
                      {!isInstructor && extInfo?.pending && (
                        <span className={`${styles.statusPill} ${styles.extensionPending}`}>
                          <span>Extension Pending</span>
                        </span>
                      )}
                      {!isInstructor && extInfo?.denied && (
                        <span className={`${styles.statusPill} ${styles.extensionDenied}`}>
                          <span>Extension Denied</span>
                        </span>
                      )}
                      {!isInstructor && extInfo?.approvedActive && (
                        <span className={`${styles.statusPill} ${styles.extensionActive}`}>
                          <span>Access until {formatExpiry(extInfo.ext.access_expires_at)}</span>
                        </span>
                      )}
                      {!isInstructor && extInfo?.approvedExpired && (
                        <span className={`${styles.statusPill} ${styles.extensionExpired}`}>
                          <span>Extension Expired</span>
                        </span>
                      )}
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.cardInfo}>
                        <h3 className={styles.cardTitle}>
                          {isWeeklyAssignment && (
                            <FiRefreshCw className={styles.recurringIcon} aria-label="Weekly recurring assignment" title="Weekly recurring assignment" />
                          )}
                          {isWeeklyAssignment && <span className={styles.recurringIconSpacer} aria-hidden="true" />}
                          <span dangerouslySetInnerHTML={{ __html: (isWeeklyAssignment && assignment.week_number ? `Week ${assignment.week_number}: ` : '') + sanitizeHtmlForViewer(assignment.title) }} />
                        </h3>

                        <div className={styles.courseInfo}>
                          <span className={styles.courseInstructorMeta}>
                            <span>{assignment.course_name || 'No course assigned'}</span>
                            {assignment.instructor_name && (
                              <>
                                <span className={styles.inlineMetaSeparator}>·</span>
                                <span>{assignment.instructor_name}</span>
                              </>
                            )}
                          </span>
                        </div>

                        <div className={styles.assignmentMetaRows}>
                          {!hideDueDate && (
                            <span className={styles.metaItem}>
                              <span className={styles.metaLabel}>Due</span>
                              <span className={styles.metaValue}>{formatAssignmentDueDate(assignment)}</span>
                            </span>
                          )}
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>Posted</span>
                            <span className={styles.metaValue}>{formatUTCToChicago(assignment.posted_date || assignment.created_at, 'MMM d \'·\' h:mm a')}</span>
                          </span>
                          {['all', 'completed'].includes(activeTab) && assignment.statusType === 'completed' && assignment.submitted_at && (
                            <span className={styles.metaItem}>
                              <span className={styles.metaLabel}>Completed</span>
                              <span className={styles.metaValue}>{formatUTCToChicago(assignment.submitted_at, 'MMM d \'·\' h:mm a')}</span>
                            </span>
                          )}
                          {(isInstructor || Number(assignment.points) > 0) && (
                            <span className={styles.metaItem}>
                              <span className={styles.metaValue}>{assignment.points} {Number(assignment.points) === 1 ? 'point' : 'points'}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardFooter} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.actions}>
                        {isInstructor ? (
                          <button
                            className={`${styles.button} ${styles.primary}`}
                            onClick={() => handleViewSubmissions(assignment)}
                          >
                            <FiBookOpen size={16} />
                            View Submissions
                          </button>
                        ) : extInfo?.submissionBlocked ? (
                          extInfo.canRequest ? (
                            <button
                              type="button"
                              className={`${styles.button} ${styles.requestExtension}`}
                              onClick={() => setExtensionTarget({
                                itemType: 'assignment',
                                itemId: assignment.id,
                                itemTitle: assignment.title,
                                courseName: assignment.course_name,
                              })}
                            >
                              <FiClock size={14} /> Request Extension
                            </button>
                          ) : (
                            <button type="button" className={`${styles.button} ${styles.secondary}`} disabled>
                              <FiClock size={14} /> Extension Pending Review
                            </button>
                          )
                        ) : (
                          <button
                            className={`${styles.button} ${['submitted', 'late', 'graded'].includes(status) && !resubmissionAllowed[assignment.id] ? styles.secondary : styles.primary} ${primaryActionClass}`}
                            onClick={() => {
                              if (['submitted', 'late', 'graded'].includes(status) && !resubmissionAllowed[assignment.id]) {
                                handleViewSubmission(assignment, assignment.week_number);
                              } else if (resubmissionAllowed[assignment.id]) {
                                handleReviseAssignment(assignment, assignment.week_number);
                              } else {
                                handleSubmitAssignment(assignment, assignment.week_number);
                              }
                            }}
                          >
                            {['submitted', 'late', 'graded'].includes(status) && !resubmissionAllowed[assignment.id] ? (
                              <><FiEye size={14} /> {primaryLabel}</>
                            ) : resubmissionAllowed[assignment.id] ? (
                              <><FiEdit3 size={14} /> {primaryLabel}</>
                            ) : (
                              <><FiFileText size={14} /> {primaryLabel}</>
                            )}
                          </button>
                        )}

                        {!isInstructor && assignment.description && (
                          <button
                            className={`${styles.showMoreActionButton} ${primaryActionClass}`}
                            onClick={() => toggleInstructionsVisibility(assignmentUiKey)}
                          >
                            {showFullText ? 'Hide Details' : 'Show Details'}
                          </button>
                        )}

                        {isInstructor && (
                          <div className={styles.cardTopActions}>
                            <button
                              type="button"
                              className={styles.topActionButton}
                              onClick={() => handleEditAssignment(assignment)}
                              aria-label="Edit assignment"
                              title="Edit"
                            >
                              <FiEdit3 size={15} />
                              <span>Edit</span>
                            </button>
                            <button
                              type="button"
                              className={`${styles.topActionButton} ${styles.dangerTopAction}`}
                              onClick={() => handleDeleteAssignment(assignment)}
                              disabled={deleteLoading}
                              aria-label="Delete assignment"
                              title="Delete"
                            >
                              <FiTrash2 size={15} />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                      </div>

                      <div className={styles.assignmentSideColumn}>
                        <div className={styles.statusPillGroupDesktop}>
                          {!isInstructor && activeTab === 'all' && showStatusBadge && (
                            <span className={`${styles.statusPill} ${styles[allTabStatus.key]}`}>
                              <span>{allTabStatus.label}</span>
                            </span>
                          )}
                          {!isInstructor && activeTab !== 'all' && displayStatus && (
                            <span className={`${styles.statusPill} ${styles[displayStatus.key]}`}>
                              {displayStatus.icon}
                              <span>{displayStatus.label}</span>
                            </span>
                          )}
                          {!isInstructor && showLateBadge && (
                            <span className={`${styles.statusPill} ${styles.late}`}>
                              <FiAlertTriangle size={14} />
                              <span>Late</span>
                            </span>
                          )}
                          {isExtraCreditAssignment && (
                            <span className={`${styles.statusPill} ${styles.extraCredit}`}>
                              <span>Extra Credit</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {showStudentInstructions && (
                    <div className={styles.detailsPanel}>
                      <div className={styles.detailsContent}>
                        {/* Instructions Section */}
                        <div className={styles.instructionsSection}>
                          <h4 className={styles.instructionsLabel}>Instructions</h4>
                          <div 
                            className={`${styles.instructionsContent} ${richTextStyles.richText} ${isInstructor && !showFullText ? styles.clamped : ''}`}
                            dangerouslySetInnerHTML={{ 
                              __html: sanitizeHtmlForViewer(assignment.description || 'No description provided') 
                            }}
                          />
                          {isInstructor && assignment.description && sanitizeHtmlForViewer(assignment.description).replace(/<[^>]*>/g, '').trim().length > 180 && (
                            <button
                              className={styles.showMoreBtn}
                              onClick={() => toggleInstructionsVisibility(assignmentUiKey)}
                            >
                              {showFullText ? 'Show Less' : 'Show More'}
                            </button>
                          )}
                        </div>

                        {/* Attachments */}
                        {assignment.pdf_attachment && (
                          <div className={styles.attachmentsSection}>
                            <div className={styles.attachmentsLabel}>
                              <FiPaperclip size={14} />
                              <span>Attachment</span>
                            </div>
                            <button
                              className={styles.attachmentLink}
                              onClick={() => handleViewAssignmentPdf(assignment)}
                            >
                              <FiFileText size={14} />
                              View PDF ({attachmentSize} KB)
                            </button>
                          </div>
                        )}

                        {/* Additional info for students */}
                        {!isInstructor && assignment.grade !== null && assignment.grade !== undefined && (
                          <div className={styles.secondaryActions}>
                            <span className={styles.metaItem}>
                              <FiCheckCircle size={14} />
                              Grade: {assignment.grade}/{assignment.points}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <FiFileText />
              <h3>No assignments found</h3>
              <p>
                {searchTerm
                  ? 'Try adjusting your search to find what you are looking for.'
                  : "You're all caught up."}
              </p>
            </div>
          )}
        </>
      )}

      {/* Inline Assignment Submission */}
      {showSubmissionInline && selectedAssignment && (
        <AssignmentSubmissionInline
          assignment={selectedAssignment}
          existingSubmission={selectedSubmission}
          onSubmissionSuccess={handleSubmissionSuccess}
          onBack={handleBackFromSubmission}
          preselectedWeek={selectedAssignment._preselectedWeek}
        />
      )}

      {/* Inline Assignment Creation */}
      {showCreateInline && (
        <CreateAssignmentInline
          onAssignmentCreated={handleAssignmentCreated}
          onBack={handleBackFromCreate}
        />
      )}

      <EditAssignmentModal
        key={`edit-${selectedAssignment?.id || 'edit-assignment'}`}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        assignment={selectedAssignment}
        onAssignmentUpdated={handleAssignmentUpdated}
      />

      <Notification
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ isOpen: false })}
      />

      <ConfirmationModal
        isOpen={Boolean(assignmentPendingDelete)}
        onClose={() => !deleteLoading && setAssignmentPendingDelete(null)}
        onConfirm={confirmDeleteAssignment}
        title="Delete Assignment"
        message={`Are you sure you want to delete "${assignmentPendingDelete?.title || 'this assignment'}"?`}
        details="This action cannot be undone and will also delete all student submissions for this assignment."
        confirmText="Delete Assignment"
        cancelText="Cancel"
        isLoading={deleteLoading}
        type="danger"
      />

      {/* PDF Viewer */}
      <PdfViewer
        isOpen={showPdfViewer}
        onClose={() => {
          setShowPdfViewer(false);
          setCurrentPdfData(null);
          setCurrentPdfFileName('');
        }}
        pdfBase64={currentPdfData}
        fileName={currentPdfFileName}
      />

      <ExtensionRequestModal
        isOpen={!!extensionTarget}
        onClose={() => setExtensionTarget(null)}
        onSubmitted={loadMyExtensions}
        itemType={extensionTarget?.itemType}
        itemId={extensionTarget?.itemId}
        itemTitle={extensionTarget?.itemTitle}
        courseName={extensionTarget?.courseName}
      />
    </div>
  );
}


export default Assignments;
